import { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Button, Card, useToast } from "../ui";
import { HttpError } from "../../services/http";
import {
  blueprintService,
  type AutonomyRun,
  type BacklogProgress,
} from "../../services/blueprint.service";

const TONE: Record<string, "ok" | "danger" | "warn" | "neutral"> = {
  completed: "ok",
  failed: "danger",
  running: "warn",
  testing: "warn",
  blocked: "danger",
  skipped: "neutral",
  ready: "neutral",
  pending: "neutral",
};

const STATUS_LABEL: Record<string, string> = {
  pending: "Aguardando base",
  ready: "Pronta",
  running: "Executando",
  testing: "Em teste",
  completed: "Concluída",
  failed: "Falhou",
  blocked: "Bloqueada",
  skipped: "Ignorada",
};

const PRIORITY_LABEL: Record<number, string> = {
  5: "Crítica",
  4: "Alta",
  3: "Média",
  2: "Baixa",
  1: "Depois",
};

/** Motivos de parada automática, em linguagem do usuário. */
const HALT_LABEL: Record<string, string> = {
  critical_error: "A etapa precisa de correção",
  provider_limit: "Limite temporário da IA",
  destructive_migration: "Alteração destrutiva de banco — precisa da sua aprovação",
  missing_credential: "Falta uma credencial",
  ambiguous_decision: "Decisão de negócio em aberto",
  max_attempts: "Três tentativas falharam na mesma etapa",
  credit_limit: "Créditos insuficientes",
  quota: "Limite do plano atingido",
  max_items: "Limite de etapas por execução atingido",
  user_stop: "Parada solicitada por você",
};

/** Backlog do wizard: execução manual etapa a etapa ou construção automática controlada. */
export default function BacklogPanel({ projectId }: { projectId: string }) {
  const toast = useToast();
  const [progress, setProgress] = useState<BacklogProgress | null>(null);
  const [run, setRun] = useState<AutonomyRun | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const state = await blueprintService.autonomy(projectId);
      setProgress(state.backlog);
      setRun(state.run);
      return state.run;
    } catch {
      return null;
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const active = run?.status === "queued" || run?.status === "running";

  useEffect(() => {
    if (!active) {
      if (timer.current) clearInterval(timer.current);
      timer.current = null;
      return;
    }

    timer.current = setInterval(() => void load(), 5000);
    return () => {
      if (timer.current) clearInterval(timer.current);
      timer.current = null;
    };
  }, [active, load]);

  if (!progress || progress.total === 0) {
    return null;
  }

  const runNext = async () => {
    setBusy(true);
    try {
      setProgress(await blueprintService.runNext(projectId));
      toast.success("Etapa executada.");
    } catch {
      toast.error("A etapa falhou. Veja o detalhe no item do backlog.");
      void load();
    } finally {
      setBusy(false);
    }
  };

  const build = async (item?: string) => {
    setBusy(true);
    try {
      const state = await blueprintService.startAutonomy(projectId, undefined, { resume: true, ...(item ? { item } : {}) });
      setRun(state.run);
      setProgress(state.backlog);
      toast.success("Construção em andamento. As etapas concluídas foram preservadas.");
    } catch (error) {
      toast.error(error instanceof HttpError ? error.message : "Não foi possível conectar. Tente novamente.");
      void load();
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    try {
      const state = await blueprintService.cancelAutonomy(projectId);
      setRun(state.run);
      toast.success("A construção vai parar após a etapa atual.");
    } catch {
      toast.error("Não foi possível parar agora.");
    }
  };

  const changePriority = async (uuid: string, priority: number) => {
    try {
      setProgress(await blueprintService.updateItem(projectId, uuid, { priority }));
    } catch {
      toast.error("Não foi possível alterar a prioridade.");
    }
  };

  const requestChange = async (uuid: string, current: string) => {
    const instruction = window.prompt("O que deve mudar nesta tarefa?", current);

    if (!instruction || instruction.trim() === current.trim()) return;

    try {
      setProgress(await blueprintService.updateItem(projectId, uuid, { instruction: instruction.trim() }));
      toast.success("Tarefa atualizada e reaberta.");
    } catch {
      toast.error("Não foi possível atualizar a tarefa.");
    }
  };

  const runItem = async (uuid: string) => {
    setBusy(true);
    try {
      setProgress(await blueprintService.runNext(projectId, uuid));
      toast.success("Tarefa executada.");
    } catch {
      toast.error("A tarefa falhou. Veja o detalhe no item.");
      void load();
    } finally {
      setBusy(false);
    }
  };

  const skip = async (uuid: string) => {
    try {
      setProgress(await blueprintService.skip(projectId, uuid));
    } catch {
      toast.error("Não foi possível ignorar o item.");
    }
  };

  const needsDecision = (run?.halt_reason === "destructive_migration" || run?.halt_reason === "ambiguous_decision") && progress.items.some((item) => item.status === "failed");
  const providerQuota = /insufficient_quota|exceeded your current quota|billing_hard_limit/i.test(run?.halt_detail ?? "");
  const rateLimited = !providerQuota && (run?.halt_reason === "provider_limit" || /HTTP 429|rate limit/i.test(run?.halt_detail ?? ""));
  const hint = providerQuota
    ? "O provedor de IA está sem saldo ou atingiu o limite de cobrança. Ajuste sua conta no provedor e retome."
    : rateLimited
      ? "A IA atingiu o limite de uso por minuto. Aguarde um pouco e retome; o progresso foi salvo."
      : run?.halt_reason === "credit_limit"
        ? "Adicione créditos na organização e retome a construção."
        : run?.halt_reason === "missing_credential"
          ? "Revise a chave e o modelo em Configurações de IA e retome."
          : needsDecision
            ? "Use Pedir alteração na etapa para esclarecer ou revisar a mudança solicitada."
            : "Você pode corrigir e tentar novamente. As etapas concluídas serão mantidas.";

  const percent = Math.round((progress.done / progress.total) * 100);

  return (
    <Card
      title="Planejamento"
      subtitle={`${progress.done} de ${progress.total} etapas concluídas (${percent}%)`}
      action={
        active ? (
          <Button size="sm" variant="ghost" onClick={stop} disabled={run?.cancel_requested}>
            {run?.cancel_requested ? "Parando…" : "Parar"}
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={runNext} disabled={busy}>
              Executar próxima
            </Button>
            <Button size="sm" onClick={() => void build()} disabled={busy || needsDecision}>
              {run ? "Retomar construção" : "Construa este sistema"}
            </Button>
          </div>
        )
      }
    >
      <div className="mb-4 h-2 w-full overflow-hidden rounded-full bg-line/40">
        <div className="h-full bg-brand" style={{ width: `${percent}%` }} />
      </div>

      {run && (
        <div className="mb-4 rounded-md border border-line px-3 py-2 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={active ? "warn" : run.status === "completed" ? "ok" : "neutral"}>
              {active ? "Construindo" : run.status === "completed" ? "Concluído" : "Parado"}
            </Badge>
            <span className="text-ink-muted">
              {run.processed} etapas processadas nesta execução (limite: {run.max_items})
              {run.failed > 0 ? ` · ${run.failed} tentativas com falha` : ""}
            </span>
          </div>
          {active && (
            <p className="mt-2 text-sm text-ink-muted" aria-live="polite">
              {run.activity ? `${run.activity.status === "running" ? "Em andamento" : "Última atividade"}: ${run.activity.title}` : "Aguardando início do processamento"}
              {run.activity?.updated_at && ` · Atualizado às ${new Date(run.activity.updated_at).toLocaleTimeString("pt-BR")}`}
            </p>
          )}
          {run.halt_reason && (
            <div className="mt-2 space-y-2 text-sm text-ink-muted">
              <p><strong className="text-ink">{providerQuota ? "Saldo do provedor" : rateLimited ? "Limite temporário da IA" : HALT_LABEL[run.halt_reason] ?? "Construção pausada"}.</strong> {hint}</p>
              {run.halt_detail && <details className="text-xs">
                <summary className="cursor-pointer">Ver detalhes técnicos</summary>
                <p className="mt-2 whitespace-pre-wrap break-words">{run.halt_detail}</p>
              </details>}
            </div>
          )}
        </div>
      )}

      <div className="space-y-4">
        {progress.stages.map((stage) => (
          <div key={stage.stage}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
              {stage.label} · {stage.done}/{stage.total}
            </p>
            <ul className="space-y-2">
              {progress.items
                .filter((item) => item.stage === stage.stage)
                .map((item) => (
                  <li
                    key={item.uuid}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-line px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm text-ink">
                        {item.position}. {item.title}
                      </p>
                      <p className="truncate text-xs text-ink-muted">
                        {item.type}
                        {item.dependencies.length > 0
                          ? ` · depende de ${item.dependencies.join(", ")}`
                          : ""}
                        {item.result && item.status !== "failed" ? ` · ${item.result}` : ""}
                      </p>
                      {item.status === "failed" && item.result && (
                        <details className="mt-1 text-xs text-ink-muted">
                          <summary className="cursor-pointer">Ver motivo da falha</summary>
                          <p className="mt-1 whitespace-pre-wrap break-words">{item.result}</p>
                        </details>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={TONE[item.status] ?? "neutral"}>
                        {STATUS_LABEL[item.status] ?? item.status}
                      </Badge>
                      {!active && item.status !== "running" && item.status !== "testing" && (
                        <>
                          <select
                            aria-label={`Prioridade de ${item.title}`}
                            className="rounded-md border border-line bg-transparent px-2 py-1 text-xs text-ink"
                            value={item.priority}
                            onChange={(event) => void changePriority(item.uuid, Number(event.target.value))}
                          >
                            {[5, 4, 3, 2, 1].map((value) => (
                              <option key={value} value={value}>
                                {PRIORITY_LABEL[value]}
                              </option>
                            ))}
                          </select>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => void requestChange(item.uuid, item.instruction)}
                          >
                            Pedir alteração
                          </Button>
                          {item.status === "failed" && !needsDecision && (
                            <Button size="sm" disabled={busy} onClick={() => void build(item.uuid)}>
                              Corrigir e tentar novamente
                            </Button>
                          )}
                          {item.status === "ready" && (
                            <Button size="sm" variant="ghost" disabled={busy} onClick={() => void runItem(item.uuid)}>
                              Executar
                            </Button>
                          )}
                          {(item.status === "ready" || item.status === "pending" || item.status === "blocked") && (
                            <Button size="sm" variant="ghost" onClick={() => skip(item.uuid)}>
                              Ignorar
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </li>
                ))}
            </ul>
          </div>
        ))}
      </div>
    </Card>
  );
}
